import { motion } from "framer-motion";
import {
  Fuel, Building2, ShoppingCart, Hotel, Car, Building, Route, Sun, ClipboardCheck,
} from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SEGMENTS, SOLAR_STATS } from "@/lib/siteContent";
import { IMAGES } from "@/lib/constants";
import PageHero from "@/components/site/PageHero";
import { QuoteForm } from "@/components/QuoteForm";

const EASE = [0.16, 1, 0.3, 1];
const SEGMENT_ICONS = [Fuel, Building2, ShoppingCart, Hotel, Car, Building, Route];

export default function SolucoesPage() {
  useSeo(
    "Soluções em Recarga de VE e Energia Solar | Eletropostos, Frotas, Comércios — HB Solar",
    "Infraestrutura de recarga para postos, frotas, supermercados, hotéis, estacionamentos, condomínios e rodovias. Energia solar fotovoltaica na Bahia."
  );

  return (
    <>
      <PageHero
        testId="solutions-page-title"
        eyebrow="Soluções"
        title="Infraestrutura de recarga para cada tipo de empreendimento"
        desc="A HB Solar analisa a infraestrutura elétrica existente e desenvolve a solução adequada para cada operação — do estacionamento de condomínio ao eletroposto de rodovia."
      />

      <section className="py-20 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div data-testid="segments-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SEGMENTS.map((s, i) => {
              const Icon = SEGMENT_ICONS[i];
              return (
                <motion.article
                  key={s.titulo}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: EASE }}
                  className="group rounded-3xl border border-slate-200/80 bg-white p-8 hover:shadow-[0_24px_48px_rgba(11,25,44,0.10)] hover:-translate-y-1 transition-all duration-500"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 transition-transform duration-500 group-hover:scale-110">
                    <Icon className="h-6 w-6" strokeWidth={2} />
                  </span>
                  <h2 className="mt-6 font-display text-xl font-semibold tracking-tight text-[#0B192C]">{s.titulo}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </motion.article>
              );
            })}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
              className="rounded-3xl bg-[#0B192C] p-8 flex flex-col justify-between"
            >
              <div>
                <ClipboardCheck className="h-8 w-8 text-amber-400" />
                <h2 className="mt-5 font-display text-xl font-semibold text-white">Análise técnica do seu local</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  Estudo da infraestrutura elétrica existente antes de qualquer
                  recomendação de equipamento.
                </p>
              </div>
              <a href="/simule" className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-extrabold text-[#0B192C] hover:bg-amber-300 transition-colors">
                Simule seu projeto
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="energia-solar" data-testid="solar-solution-section" className="relative grain overflow-hidden bg-[#0B192C] py-24">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-400 mb-4">
              <Sun className="inline h-4 w-4 mr-1 -mt-0.5" /> Energia solar fotovoltaica
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight text-white">
              Geração solar para reduzir sua conta de luz em{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
                {SOLAR_STATS.economia}
              </span>
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300">
              Projeto, instalação e homologação completos para residências, comércios
              e propriedades rurais na Bahia. São {SOLAR_STATS.projetos} projetos
              instalados com garantia de {SOLAR_STATS.garantia} dos painéis.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-slate-300">
              <li>· Residências, comércios, indústrias e agronegócio</li>
              <li>· Projeto e homologação junto à concessionária inclusos</li>
              <li>· Simulação de economia gratuita abaixo</li>
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="rounded-3xl overflow-hidden border border-white/15"
          >
            <img src={IMAGES.installation} alt="Instalação de painéis solares pela equipe HB Solar" className="h-80 w-full object-cover" loading="lazy" />
          </motion.div>
        </div>
      </section>

      <QuoteForm />
    </>
  );
}
